import type { Audience, PriceValue, PricingContent } from '../pricing';

/**
 * Arabic pricing page content.
 *
 * Row labels and group titles come from section 14 of the nsave Arabic
 * Localization Pack. The following were NOT in the pack and are translated here —
 * flag them first in a native review:
 *   • the per-month cadence suffix
 *   • every tooltip (`info`) string
 *   • the whole Business table (setup fee, FX row, SWIFT min/max, "Coming soon")
 *
 * ⚠️ The pack's section 2 records unresolved *pricing* conflicts (Pro SWIFT listed
 * as both $3.50 and $9.99; EUR account price differing between site and Help
 * Centre). The figures below mirror the English table verbatim and are deliberately
 * not reinterpreted — Product still owns the canonical numbers.
 *
 * Amounts, percentages and rail names stay in Latin script per the pack's currency
 * rule; `PricingTable` renders every value inside `dir="ltr"` so they read
 * correctly inside RTL rows.
 */

const free: PriceValue = { text: 'مجانًا', free: true };

const receiveInfo = 'من غير رسوم على استقبال USD عبر SWIFT أو ACH.';
const withdrawInfo = 'الرسوم بتختلف حسب المبلغ والعملة.';
const rewardsInfo = 'نسبة متغيرة، تُضاف يوميًا.';
// Rendered directly after the amount ("$9.99" + "/شهر"), mirroring the English
// "/m". ⚠️ Authored here — the pack has no cadence string; flag it in review.
const perMonth = '/شهر';

const none: PriceValue = { text: '—' };

const included: PriceValue = { text: 'مشمول', included: true };

// ⚠️ Authored here, not in the pack: the plan features and coming soon labels
// and their detail lines. Flag them in a native review.
const personal: PricingContent = {
  plans: [
    { name: 'Essential', price: 'مجانًا', cadence: '' },
    { name: 'Pro', price: '$5.99', cadence: perMonth },
    { name: 'Prestige', price: '$29.99', cadence: perMonth },
  ],
  groups: [
    {
      title: 'مميزات الباقة',
      rows: [
        // The Platinum and Infinite tier names stay in Latin script like the other
        // proper names in this table (SWIFT, ACH, SEPA, IBAN, nsave).
        { label: 'كارت Platinum', values: [{ text: '$1.99' }, included, included], cards: true },
        { label: 'كارت Infinite', values: [none, none, included], cards: true },
        {
          label: 'كاش باك على مشتريات الكارت',
          values: [
            none,
            { text: '0.5%' },
            { text: '1%', details: ['على أول $5k شهريًا', 'وبعدين 0.5%'] },
          ],
          cards: true,
        },
        {
          label: 'التحويل بين USD وEUR وGBP',
          values: [{ text: '1%' }, { text: '0.5%' }, { text: '0.5%' }],
        },
        {
          label: 'حسابات تحويل دولي USD وEUR',
          values: [
            {
              text: '$5.99',
              details: ['لكل حساب', 'وبعدين $0.99 شهريًا'],
              info: 'سكان الاتحاد الأوروبي يفتحوا EUR وسكان الخليج يفتحوا USD مجانًا.',
            },
            included,
            included,
          ],
        },
        {
          label: 'حساب GBP',
          values: [
            {
              text: '$5.99',
              details: ['وبعدين $0.99 شهريًا'],
              info: 'مجانًا لسكان المملكة المتحدة.',
            },
            {
              text: '$5.99',
              details: ['وبعدين $0.99 شهريًا'],
              info: 'مجانًا لسكان المملكة المتحدة.',
            },
            included,
          ],
        },
        {
          label: 'عمولة الاستثمار',
          values: [
            { text: '0.2%', details: ['بحد أدنى $0.25'] },
            { text: '0.2%', details: ['بحد أدنى $0.25', 'بحد أقصى $15'] },
            { text: '0.2%', details: ['بحد أدنى $0.25', 'بحد أقصى $10'] },
          ],
        },
        { label: 'أوامر الحد والإيقاف', values: [none, none, included] },
        { label: 'اختار @ntag بتاعك', values: [none, included, included] },
      ],
    },
    {
      title: 'قريبًا',
      comingSoon: true,
      rows: [{ label: 'عائد على رصيد USD', values: [none, { text: '2%' }, { text: '4%' }] }],
    },
    {
      title: 'استقبال الأموال',
      shared: true,
      rows: [
        { label: 'بيانات ACH لحساب USD: رقم التوجيه ورقم الحساب', values: [free] },
        {
          label: 'استقبال USD عبر SWIFT أو ACH',
          values: [{ text: 'مجانًا', free: true, info: receiveInfo }],
        },
        { label: 'إيداع بعملة مستقرة', values: [free] },
      ],
    },
    {
      title: 'إرسال الأموال',
      shared: true,
      rows: [
        { label: 'تحويلات بين مستخدمي nsave', values: [free] },
        {
          label: 'تحويل لعملتك المحلية',
          values: [{ text: 'اعرف سعر النهارده', href: '/send-money-home', info: withdrawInfo }],
        },
        { label: 'إرسال USD دوليًا عبر ACH', values: [{ text: '$0.5' }] },
        { label: 'إرسال USD دوليًا عبر SWIFT', values: [{ text: '$12.99' }] },
        { label: 'إرسال GBP عبر FPS', values: [{ text: '$1' }] },
        { label: 'إرسال EUR عبر SEPA', values: [{ text: '$1' }] },
        { label: 'إرسال لمحفظة عملات مستقرة', values: [{ text: '1%' }] },
        { label: 'إرسال USD لحساب بنكي في مصر', values: [{ text: '$3.50' }] },
      ],
    },
    {
      title: 'الدفع بالكارت',
      shared: true,
      cards: true,
      rows: [
        { label: 'الدفع بالكارت بعملة USD', values: [free] },
        {
          label: 'الدفع بالكارت بعملة مختلفة عن عملة رصيد الكارت',
          values: [{ text: '1.5%' }],
        },
      ],
    },
  ],
};

const business: PricingContent = {
  plans: [{ name: 'Standard', price: '$0.00', cadence: perMonth }],
  groups: [
    {
      title: 'الحسابات',
      rows: [
        { label: 'رسوم فتح الحساب', values: [{ text: '$200.00' }] },
        { label: 'بيانات ACH لحساب USD: رقم التوجيه ورقم الحساب', values: [free] },
        { label: 'بيانات التحويل الدولي لحساب USD: IBAN', values: [free] },
        { label: 'حساب GBP', values: [free] },
        { label: 'حساب EUR', values: [free] },
        { label: 'تحويل العملات (GBP ↔ EUR ↔ USD)', values: [{ text: '0.6%' }] },
      ],
    },
    {
      title: 'إرسال الأموال',
      rows: [
        { label: 'إرسال GBP عبر FPS', values: [{ text: '$1' }] },
        {
          label: 'إرسال USD أو EUR عبر SWIFT',
          // Amounts are written "14.99 دولار" rather than "$14.99" on purpose: a
          // leading "$" inside an Arabic sentence gets reordered by the bidi
          // algorithm and ends up detached from its number (and drags the closing
          // bracket with it). Spelling the currency avoids the mixed-direction run.
          values: [{ text: '0.3% ** (الحد الأدنى 14.99 دولار، الحد الأقصى 24.99 دولار)' }],
        },
      ],
    },
    {
      title: 'المكافآت',
      rows: [
        {
          label: 'مكافآت سنوية على رصيد USD (قريبًا)',
          values: [{ text: '3.2%', info: rewardsInfo }],
        },
      ],
    },
  ],
};

export const pricing: Record<Audience, PricingContent> = { personal, business };
